import { LinkOutlined } from '@ant-design/icons';
import * as AntIcons from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { Link, history, request as umiRequest } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import UserAvatar from '@/components/UserAvatar';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { getMyUserSettings } from '@/services/sys_user_settings';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { getApiBaseUrl } from '@/utils/api';
import '@ant-design/v5-patch-for-react-19';

type SysMenuNode = {
  name?: string;
  path: string;
  icon?: string | React.ReactNode;
  permission_code?: string;
  children?: SysMenuNode[];
  hideInMenu?: boolean;
};

const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  permissionCodes?: string[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    let resolvedSettings: Partial<LayoutSettings> = defaultSettings as Partial<LayoutSettings>;
    try {
      const userSettings = await getMyUserSettings({ skipErrorHandler: true });
      const themeStyle = userSettings?.theme_style;
      resolvedSettings = {
        ...resolvedSettings,
        navTheme: themeStyle === 'dark' ? 'realDark' : 'light',
      };
    } catch (_error) {}
    let permissionCodes: string[] = [];
    try {
      const res = await umiRequest<{ permission_codes: string[] }>(
        '/api/users/me/permission-codes',
        { skipErrorHandler: true },
      );
      permissionCodes = Array.isArray(res?.permission_codes)
        ? res.permission_codes
        : [];
    } catch (_error) {}

    return {
      fetchUserInfo,
      currentUser,
      permissionCodes,
      settings: resolvedSettings,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const permissionCodes = initialState?.permissionCodes ?? [];
  const permissionSet = new Set(permissionCodes);
  const canAdmin = Boolean(initialState?.currentUser?.access === 'admin');
  const hasPermission = (code?: string) =>
    !code || canAdmin || permissionSet.has(code);

  const formatIcon = (name: string) =>
    name.replace(name[0], name[0].toUpperCase()).replace(/-(\w)/g, (_, c) => c.toUpperCase());

  const resolveMenuIcon = (raw: string): React.ReactNode | undefined => {
    const resolved = (raw || '').trim();
    if (!resolved) return undefined;

    const base = formatIcon(resolved);
    const candidates = [
      base,
      ...(base.endsWith('Outlined') ? [] : [`${base}Outlined`]),
      ...(base.endsWith('Filled') ? [] : [`${base}Filled`]),
      ...(base.endsWith('TwoTone') ? [] : [`${base}TwoTone`]),
    ];
    for (const key of candidates) {
      const IconComp = (AntIcons as any)[key];
      if (IconComp) return React.createElement(IconComp);
    }
    return undefined;
  };

  const mapMenuIcons = (menuData: SysMenuNode[]): SysMenuNode[] => {
    return menuData.map((item) => {
      const next: SysMenuNode = { ...item };
      if (typeof next.icon === 'string') {
        next.icon = resolveMenuIcon(next.icon);
      }
      if (Array.isArray(next.children)) {
        next.children = mapMenuIcons(next.children);
      }
      return next;
    });
  };

  const filterMenuByPermission = (nodes: SysMenuNode[]): SysMenuNode[] => {
    return (nodes || [])
      .filter((n) => !n.hideInMenu)
      .map((n) => {
        const children = Array.isArray(n.children)
          ? filterMenuByPermission(n.children)
          : [];
        return { ...n, children };
      })
      .filter((n) => {
        if (Array.isArray(n.children) && n.children.length) return true;
        return hasPermission(n.permission_code);
      });
  };

  const getPathDepth = (path?: string) => (path ? path.split('/').filter(Boolean).length : 0);
  const shouldInjectIcon = (item: any) => getPathDepth(item?.path) > 1;

  return {
    actionsRender: () => [
      <Question key="doc" />,
      isDev ? (
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>OpenAPI 文档</span>
        </Link>
      ) : null,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      title: <AvatarName />,
      render: (_, _avatarChildren) => {
        return (
          <AvatarDropdown menu>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserAvatar />
              <AvatarName />
            </span>
          </AvatarDropdown>
        );
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menu: {
      locale: true,
      request: async () => {
        const res = await umiRequest<{ data: SysMenuNode[] }>(
          '/api/sys-menus/tree',
        );
        return mapMenuIcons(filterMenuByPermission(res.data));
      },
    },
    menuItemRender: (item: any, dom: any) => {
      const content =
        item?.icon && shouldInjectIcon(item) ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {item.icon}
            {dom}
          </span>
        ) : (
          dom
        );
      if (item?.children?.length) return content;
      if (!item?.path) return content;
      if (item?.isUrl) {
        return (
          <a href={item.path} target={item.target} rel="noreferrer">
            {content}
          </a>
        );
      }
      return <Link to={item.path}>{content}</Link>;
    },
    subMenuItemRender: (item: any, dom: any) => {
      if (!item?.icon) return dom;
      if (!shouldInjectIcon(item)) return dom;
      if (!React.isValidElement(dom)) return dom;
      const el = dom as React.ReactElement<any>;
      return React.cloneElement(
        el,
        el.props as any,
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {item.icon}
          {el.props?.children}
        </span>,
      );
    },
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState: any) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...(() => {
    const apiBase = getApiBaseUrl();
    return apiBase ? { baseURL: apiBase } : {};
  })(),
  ...errorConfig,
};
